import React, { CSSProperties, memo, useEffect, useState } from 'react';
import { UMInputNumber } from '@urbanmetrix/ui-kit';
import { Box, IconButton, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material';
import { AlignVerticalCenter01, ChevronSelectorVertical } from '@untitledui-pro/icons/line';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { SOLAR_MONTHS } from '@gui/constants/SolarMonths';
import { Panels } from '@gui/constants/Panels';
import usePersistentStore, { getGlobalState, useGlobalBucket } from '@gui/store/persistent/persistentStore';
import type { DeepPartial, SolarEnergyResourcesState } from '@visualizer/types';
import Grid from 'Geodata/grid';
import { useResourceStore } from 'store/ResourceStore';
import { getLoadingStatus, LoadingStatus } from 'store/LoadingStore';
import { kWhToMJ } from 'Utils/MathCustomUtils';
import { formatMetricNumber } from 'Utils/MetricFormatting';

type ProjectSpecProps = {
    isCollapsed?: boolean;
    onToggleCollapse: () => void;
    panelsExpanded: Record<string, boolean>;
    style?: CSSProperties;
};

const hasGridMetrics = (params: unknown): params is { gridResolution?: number | null; gridHeight?: number | null } => {
    return typeof params === 'object' && params !== null;
};

const ProjectSpecifications: React.FC<ProjectSpecProps> = ({ isCollapsed = false, onToggleCollapse, panelsExpanded, style }) => {
    const [activeTab, setActiveTab] = useState<'projectSpec' | 'energyDemand'>('projectSpec');
    const buildingPatterns = useGlobalBucket((state) => state.solarEnergyResources.buildingPatterns);
    const energyDemand = useGlobalBucket((state) => state.solarEnergyResources.energyDemand);
    const solarParams = useGlobalBucket((state) => state.solarEnergyResources.solarParams);
    const gridParams = useResourceStore((state) => state.gridResources?.params);
    const setPanel = usePersistentStore((s) => s.setPanel);
    const setSolarEnergyPanel = (updates: DeepPartial<SolarEnergyResourcesState>) => setPanel(updates, Panels.SOLAR_ENERGY_RESOURCES);
    const expandedCount = Object.values(panelsExpanded).filter(Boolean).length;
    const disableToggle = !isCollapsed && expandedCount === 1;
    const toggleLabel = isCollapsed ? 'Expand panel' : 'Collapse panel';

    const tinyLabelSx = {
        fontSize: '10px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    };
    const rowLabelSx = {
        fontSize: '10px',
        flex: 1,
        minWidth: 0,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
    };

    const rightFieldSx = {
        ml: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: '0 1 56%',
        minWidth: 120,
        maxWidth: '100%',
    };

    const defaultYearlyRateByUse: Record<string, number> = {
        Residential: 72,
        Office: 95,
    };

    const loadingStatus = getLoadingStatus();
    useEffect(() => {
        if (loadingStatus === LoadingStatus.VISUALIZER_READY && buildingPatterns.grossFloorSurface.value === -1) {
            const floorsNumber = (Grid.gridZ * Grid.gridRes) / 3;
            const grossFloorSurface = Math.round(0.5 * Grid.legalBorderArea * floorsNumber);

            setSolarEnergyPanel({
                buildingPatterns: {
                    grossFloorSurface: { value: grossFloorSurface },
                },
            });
        }
    }, [loadingStatus, buildingPatterns.grossFloorSurface.value]);

    const handleGrossFloorSurfaceChange = (value: number) => {
        const nextValue = Number.isFinite(value) ? Math.max(0, value) : 0;
        setSolarEnergyPanel({
            buildingPatterns: {
                grossFloorSurface: { value: nextValue },
            },
        });
    };

    const handleBuildingUseChange = (use: string) => {
        setSolarEnergyPanel({
            buildingPatterns: {
                use,
            },
        });
    };

    const handleYearlyRateModeChange = (mode: 'default' | 'manual') => {
        setSolarEnergyPanel({
            energyDemand: {
                yearlyRateMode: mode,
            },
        });
    };

    const handleYearlyRateManualChange = (value: number) => {
        setSolarEnergyPanel({
            energyDemand: {
                yearlyRateManual: value,
            },
        });
    };

    const handleTotalPeakMonthChange = (month: string) => {
        setSolarEnergyPanel({
            energyDemand: {
                totalPeakMonth: month,
            },
        });
    };

    const handleTotalPeakRateChange = (value: number) => {
        setSolarEnergyPanel({
            energyDemand: {
                totalPeakRate: value,
            },
        });
    };

    const handleHeatingPeakMonthChange = (month: string) => {
        setSolarEnergyPanel({
            energyDemand: {
                heatingPeakMonth: month,
            },
        });
    };

    const handleHeatingPeakRateChange = (value: number) => {
        setSolarEnergyPanel({
            energyDemand: {
                heatingPeakRate: value,
            },
        });
    };

    const defaultYearlyRate = defaultYearlyRateByUse[buildingPatterns.use] ?? 72;
    const yearlyRateKwhM2 = energyDemand.yearlyRateMode === 'manual' ? Number(energyDemand.yearlyRateManual || 0) : defaultYearlyRate;
    const grossFloorSurface = Number(buildingPatterns.grossFloorSurface.value ?? 0);
    const totalYearlyDemandKwh = Math.max(0, grossFloorSurface) * Math.max(0, yearlyRateKwhM2);
    const gridResolution = hasGridMetrics(gridParams) && typeof gridParams.gridResolution === 'number' ? gridParams.gridResolution : null;
    const gridHeight = hasGridMetrics(gridParams) && typeof gridParams.gridHeight === 'number' ? gridParams.gridHeight : null;

    useEffect(() => {
        const yearlyDemand = solarParams.unitOfMeasure === 0 ? totalYearlyDemandKwh : kWhToMJ(totalYearlyDemandKwh);
        const prev = getGlobalState().solarEnergyResources.yearlyDemand;
        if (prev !== yearlyDemand) {
            setSolarEnergyPanel({ yearlyDemand });
        }
    }, [totalYearlyDemandKwh, solarParams.unitOfMeasure]);

    return (
        <div className={`data-panel data-panel--project-spec ${isCollapsed ? 'data-panel--collapsed' : ''}`} style={style}>
            <div className="data-panel-tabs">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                    }}
                >
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value) => {
                                if (isCollapsed) return;
                                setActiveTab(value as 'projectSpec' | 'energyDemand');
                            }}
                            aria-label="Project specifications tabs"
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    gap: 1,
                                    mr: 1.5,
                                },
                            }}
                        >
                            <Tab value="projectSpec" label="PROJECT SPECS" disabled={isCollapsed} />
                            <Tab value="energyDemand" label="ENERGY DEMAND" disabled={isCollapsed} />
                        </Tabs>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onToggleCollapse}
                        disabled={disableToggle}
                        aria-label={toggleLabel}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                transition: 'transform 120ms ease',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            }}
                        >
                            {isCollapsed ? (
                                <ChevronSelectorVertical className="icon-size-small" />
                            ) : (
                                <AlignVerticalCenter01 className="icon-size-small" />
                            )}
                        </Box>
                    </IconButton>
                </Box>
            </div>

            <div className="data-panel-content">
                <div className="data-panel-scroll">
                    {activeTab === 'projectSpec' ? (
                        <Box sx={{ px: 1, py: 0.5 }}>
                            {gridResolution != null ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Typography sx={tinyLabelSx}>Grid Resolution</Typography>
                                    <Box sx={rightFieldSx}>
                                        <Typography className="project-spec-readonly-value">
                                            {`${formatMetricNumber(gridResolution)} [m]`}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : null}

                            {gridHeight != null ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mt: 0.75 }}>
                                    <Typography sx={tinyLabelSx}>Grid Height</Typography>
                                    <Box sx={rightFieldSx}>
                                        <Typography className="project-spec-readonly-value">{`${formatMetricNumber(gridHeight)} [m]`}</Typography>
                                    </Box>
                                </Box>
                            ) : null}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 0.75, mt: 0.75 }}>
                                <CustomTooltip mode="hover" side="left" title="Default = 0.5 x Legal Border Area x (Grid Height / 3).">
                                    <Typography sx={{ ...tinyLabelSx, cursor: 'help' }}>Gross Floor Surface</Typography>
                                </CustomTooltip>
                                <Box sx={rightFieldSx}>
                                    <UMInputNumber
                                        value={
                                            buildingPatterns.grossFloorSurface.value == null ||
                                            Number(buildingPatterns.grossFloorSurface.value) < 0
                                                ? ''
                                                : Number(buildingPatterns.grossFloorSurface.value)
                                        }
                                        min={buildingPatterns.grossFloorSurface.minValue}
                                        max={buildingPatterns.grossFloorSurface.maxValue}
                                        maxDecimals={1}
                                        onChange={(_, value) => {
                                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                                handleGrossFloorSurfaceChange(value);
                                            }
                                        }}
                                        style={{ flex: '1 1 auto', minWidth: 0 }}
                                        inputMinHeight={22}
                                        width={105}
                                        localEn={true}
                                        inputFontSize="10px"
                                        inputPadding="2px 6px"
                                    />
                                    <Typography sx={tinyLabelSx}>[m²]</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mt: 0.75 }}>
                                <Typography sx={tinyLabelSx}>Building Use</Typography>
                                <Box sx={rightFieldSx}>
                                    <Select
                                        size="small"
                                        value={buildingPatterns.use}
                                        onChange={(e) => handleBuildingUseChange(e.target.value)}
                                        sx={{
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            '& .MuiInputBase-root, &.MuiInputBase-root': {
                                                height: 22,
                                                fontSize: '10px',
                                            },
                                            '& .MuiSelect-select': {
                                                py: 0.25,
                                            },
                                        }}
                                    >
                                        <MenuItem value="Residential" sx={{ fontSize: '10px' }}>
                                            Residential
                                        </MenuItem>
                                        <MenuItem value="Office" sx={{ fontSize: '10px' }}>
                                            Office
                                        </MenuItem>
                                    </Select>
                                    <Typography sx={{ ...tinyLabelSx, visibility: 'hidden' }}>[m²]</Typography>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ px: 1, py: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography sx={tinyLabelSx}>Rate Source</Typography>
                                <Box sx={rightFieldSx}>
                                    <Select
                                        size="small"
                                        value={energyDemand.yearlyRateMode}
                                        onChange={(e) => handleYearlyRateModeChange(e.target.value as 'default' | 'manual')}
                                        sx={{
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            '& .MuiInputBase-root, &.MuiInputBase-root': {
                                                height: 22,
                                                fontSize: '10px',
                                            },
                                            '& .MuiSelect-select': {
                                                py: 0.25,
                                            },
                                        }}
                                    >
                                        <MenuItem value="default" sx={{ fontSize: '10px' }}>
                                            By Use
                                        </MenuItem>
                                        <MenuItem value="manual" sx={{ fontSize: '10px' }}>
                                            Manual
                                        </MenuItem>
                                    </Select>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', mt: 0.75 }}>
                                <Typography sx={rowLabelSx}>Total Yearly Energy Demand Rate</Typography>
                                <Box sx={{ ...rightFieldSx, mt: 0.1 }}>
                                    <UMInputNumber
                                        value={yearlyRateKwhM2}
                                        maxDecimals={1}
                                        onChange={(_, value) => {
                                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                                handleYearlyRateManualChange(value);
                                            }
                                        }}
                                        disabled={energyDemand.yearlyRateMode !== 'manual'}
                                        style={{ flex: '1 1 auto', minWidth: 0 }}
                                        inputMinHeight={22}
                                        width={105}
                                        inputFontSize="10px"
                                        inputPadding="2px 6px"
                                    />
                                    <Typography sx={tinyLabelSx}>[kWh/m²]</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', mt: 0.75 }}>
                                <Typography sx={rowLabelSx}>Total Yearly Energy Demand</Typography>
                                <Box
                                    sx={{
                                        ...rightFieldSx,
                                        mt: 0.1,
                                    }}
                                >
                                    <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                                        <Typography sx={{ ...tinyLabelSx, fontWeight: 600 }}>
                                            {formatMetricNumber(totalYearlyDemandKwh)}
                                        </Typography>
                                    </Box>
                                    <Typography sx={tinyLabelSx}>[kWh/y]</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ width: '100%', mt: 0.75 }}>
                                <Typography sx={rowLabelSx}>Total Energy Demand Peak</Typography>
                                <Box sx={{ ...rightFieldSx, mt: 0.35 }}>
                                    <Select
                                        size="small"
                                        value={energyDemand.totalPeakMonth}
                                        onChange={(e) => handleTotalPeakMonthChange(e.target.value)}
                                        sx={{
                                            width: 86,
                                            flexShrink: 0,
                                            '& .MuiInputBase-root, &.MuiInputBase-root': {
                                                height: 22,
                                                fontSize: '10px',
                                            },
                                            '& .MuiSelect-select': {
                                                py: 0.25,
                                            },
                                        }}
                                    >
                                        {SOLAR_MONTHS.map((month) => (
                                            <MenuItem key={month} value={month} sx={{ fontSize: '10px' }}>
                                                {month}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <UMInputNumber
                                        value={energyDemand.totalPeakRate}
                                        maxDecimals={1}
                                        onChange={(_, value) => {
                                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                                handleTotalPeakRateChange(value);
                                            }
                                        }}
                                        style={{ flex: '0 1 42%', minWidth: 78 }}
                                        inputMinHeight={22}
                                        width={78}
                                        inputFontSize="10px"
                                        inputPadding="2px 6px"
                                    />
                                    <Typography sx={tinyLabelSx}>[kWh/m²]</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ width: '100%', mt: 0.75 }}>
                                <Typography sx={rowLabelSx}>Heating Demand Peak</Typography>
                                <Box sx={{ ...rightFieldSx, mt: 0.35 }}>
                                    <Select
                                        size="small"
                                        value={energyDemand.heatingPeakMonth}
                                        onChange={(e) => handleHeatingPeakMonthChange(e.target.value)}
                                        sx={{
                                            width: 86,
                                            flexShrink: 0,
                                            '& .MuiInputBase-root, &.MuiInputBase-root': {
                                                height: 22,
                                                fontSize: '10px',
                                            },
                                            '& .MuiSelect-select': {
                                                py: 0.25,
                                            },
                                        }}
                                    >
                                        {SOLAR_MONTHS.map((month) => (
                                            <MenuItem key={month} value={month} sx={{ fontSize: '10px' }}>
                                                {month}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <UMInputNumber
                                        value={energyDemand.heatingPeakRate}
                                        maxDecimals={1}
                                        onChange={(_, value) => {
                                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                                handleHeatingPeakRateChange(value);
                                            }
                                        }}
                                        style={{ flex: '0 1 42%', minWidth: 78 }}
                                        inputMinHeight={22}
                                        width={78}
                                        inputFontSize="10px"
                                        inputPadding="2px 6px"
                                    />
                                    <Typography sx={tinyLabelSx}>[kWh/m²]</Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(ProjectSpecifications);
